import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { ChartContainer } from "@/components/charts/ChartContainer";
import { formatCurrency } from "@/utils/format";
import { CustoMensalPorCCStat, CustoUnitarioCCStat } from "../chartUtils";
import { lineHoverOnly } from "@/utils/rechartsInteraction";

/* ─── Paleta de cores ────────────────────────────────────────────────────── */
const LINE_COLORS = [
  "#6366f1", "#e74c3c", "#10b981", "#f59e0b",
  "#3b82f6", "#8b5cf6", "#ec4899", "#14b8a6",
  "#f97316", "#84cc16",
];

/* ─── Tooltip customizado ────────────────────────────────────────────────── */
interface TooltipProps {
  active?:  boolean;
  payload?: { value: number; name: string; color: string }[];
  label?:   string;
}

function CustomTooltip({ active, payload, label }: TooltipProps) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-modal p-3 min-w-[240px] max-h-64 overflow-y-auto">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{label}</p>
      {payload
        .filter((p) => p.value > 0)
        .sort((a, b) => b.value - a.value)
        .map((item) => (
          <div key={item.name} className="flex items-center justify-between gap-4 text-sm py-0.5">
            <span className="flex items-center gap-1.5 min-w-0">
              <span className="w-2.5 h-2.5 flex-shrink-0 rounded-full" style={{ background: item.color }} />
              <span className="text-slate-600 truncate">{item.name}</span>
            </span>
            <span className="font-semibold text-slate-800 flex-shrink-0">{formatCurrency(item.value)}</span>
          </div>
        ))}
    </div>
  );
}

/* ─── Componente ─────────────────────────────────────────────────────────── */
interface Props {
  data:    CustoMensalPorCCStat[];
  ccStats: CustoUnitarioCCStat[];
  loading: boolean;
}

export function EvolucaoCustoMultiCC({ data, ccStats, loading }: Props) {
  /* Exibe no máximo 8 CCs para não poluir o gráfico */
  const visibleCCs = ccStats.slice(0, 8);

  return (
    <ChartContainer
      title="Evolução do Custo Unitário Acumulado por Obra"
      subtitle="Custo acumulado por unidade (R$/casa) ao longo do tempo"
      loading={loading}
      empty={!loading && (data.length === 0 || ccStats.length === 0)}
      height={320}
    >
      <LineChart data={data} margin={{ top: 4, right: 20, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
        <XAxis
          dataKey="mes"
          tick={{ fontSize: 11, fill: "#94a3b8" }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          tick={{ fontSize: 11, fill: "#94a3b8" }}
          axisLine={false}
          tickLine={false}
          width={60}
          tickFormatter={(v: number) =>
            v >= 1_000_000
              ? `${(v / 1_000_000).toFixed(1)}M`
              : `${(v / 1_000).toFixed(0)}K`
          }
        />
        <Tooltip content={<CustomTooltip />} cursor={{ stroke: "#e2e8f0", strokeWidth: 1 }} />
        <Legend
          wrapperStyle={{ fontSize: 10, paddingTop: 8 }}
          iconType="circle"
          iconSize={8}
          formatter={(value: string) => (
            <span className="text-slate-600">{value.length > 24 ? value.slice(0, 24) + "…" : value}</span>
          )}
        />
        {visibleCCs.map((cc, i) => (
          <Line
            key={cc.costCenterId}
            type="monotone"
            dataKey={cc.name}
            stroke={LINE_COLORS[i % LINE_COLORS.length]}
            strokeWidth={2}
            dot={false}
            {...lineHoverOnly(LINE_COLORS[i % LINE_COLORS.length], { r: 7 })}
          />
        ))}
      </LineChart>
    </ChartContainer>
  );
}
